<script setup lang="ts">
import {type TaskForm, TaskFormDefault, type TaskType} from '~/types/Task'
import type { FormContext } from 'vee-validate'
import {endpoints} from "~/constants/endpoints";
import {useToast} from "~/components/ui/toast";
import { Dialog, DialogTrigger, DialogContent, DialogClose } from '~/components/ui/dialog'
import { Button } from '~/components/ui/button'
import { Form } from 'vee-validate'
import { Alert, AlertTitle, AlertDescription } from '~/components/ui/alert'
import EditFieldset from '~/components/pages/tasks/EditFieldset.vue'

const { toast } = useToast();
const { t } = useI18n();
const userStore = useUserStore()
const Loader = useIsLoadingStore()
const gtm = useGtm()

const props = defineProps({
  master_id: {
    type: [Number, String],
    required: true
  },
  onlySpecificationIds: {
    type: Array as PropType<String[]>,
  },
  btn_class: {
    type: String
  },
  hideBtn: {
    type: Boolean,
    default: false
  }
});

const formData = ref<TaskForm>({...TaskFormDefault});
  
async function createTaskSubmit(data: TaskForm, context: FormContext) {
    if(!userStore.user?.documentId) return
    Loader.set(true)
    const taskId = await createTask(data, context);
    if(!taskId) return
    const response = await setTaskExecutor(taskId)
    Loader.set(false)
    toast({
        title: t('Successfully suggested'),
        variant: 'success',
    })
    navigateTo({name: 'office'})
};

async function createTask(data: TaskForm, context: FormContext) {
    if(!userStore.user?.documentId) return null
    data.owner = userStore.user?.documentId
    
    return await fetchAuth<TaskType>(endpoints.user.tasks.create, {
        method: 'POST',
        body: data,
    }).then(({data, meta}) => {
        if(gtm){
            gtm.trackEvent({
                event: 'order_created',
                category: 'custom_actions',
                action: 'submit',
                label: 'Cоздание заказа',
                noninteraction: false,
            })
        }
        return data?.documentId
    }).catch((error) => {
        handleValidationErrors(error, context.setErrors);
    })
}

async function setTaskExecutor(taskId: string) {
    const body = {
        portfolioId: props.master_id,
        taskId: taskId,
    }
    return await fetchAuth<string>(endpoints.user.tasks.suggest, {
        method: 'POST',
        body: body,
    }).then(({data, meta}) => {
        console.log(data)
        return data
    }).catch((error) => {
        console.error(error)
    })
}

</script>

<template>
  <Dialog>
    <DialogTrigger v-if="!hideBtn" as-child>
      <Button class="bg-primary-500 rounded-lg px-3 py-2 text-sm font-medium text-white hover:bg-primary-600" :class="btn_class">
        {{ $t("Suggest order") }}
      </Button>
    </DialogTrigger>
    <DialogContent class="ys-modal-dialog !max-w-[600px]">
      <div class="ys-modal-content grid items-center place-items-center px-4 pt-10">

        <p class="font-medium text-xl mb-3">{{ $t("Suggest order") }}</p>
        <p class="ys-description-text">{{ $t("Before appointing a candidate, review their portfolio, agree on timing and cost of services.") }}</p>
        <Form v-slot="{ errors }" @submit="createTaskSubmit" class="w-full">
          <EditFieldset v-model="formData" class="mt-4" :onlySpecificationIds="onlySpecificationIds" />
          <div v-if="errors?.general" class="px-4 pb-5">
              <Alert variant="destructive" class="bg-red-50 border-none">
                  <Icon name="ri:error-warning-line" size="16px" class="text-danger" />
                  <AlertTitle>{{ $t('Error') }}</AlertTitle>
                  <AlertDescription>{{ errors.general }}</AlertDescription>
              </Alert>
          </div>
        

          <div class="flex justify-center items-center gap-2 py-4 mt-4 w-full">
            <DialogClose as-child>
              <Button
                  type="button"
                  variant="outline"
                  class="w-full"
                  >
                {{ $t("Dismiss") }}
              </Button>
            </DialogClose>
              <Button 
                  :disabled="Loader.isLoading"
                  type="submit"
                  variant="primary"
                  class="w-full">
                  <Icon v-if="Loader.isLoading" name="svg-spinners:ring-resize" />
                  <span v-else>{{ $t("Suggest order") }}</span>
              </Button>
          </div>
        </Form>
        
      </div>
    </DialogContent>
  </Dialog>
</template>

<style scoped lang="scss">

.ys-info-notice {
  width: 40px;
  height: 40px;
  padding: 8px;
  gap: 8px;
  border-radius: 10px;
  background: rgba(235, 241, 255, 1);
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  color: rgba(10, 13, 20, 1);

  .menu-item-icon {
    color: rgba(45, 159, 117, 1);
  }
}


.modal-footer {
  height: 64px;
  padding: 16px 20px 16px 20px;
  border-top: 1px solid rgba(226, 228, 233, 1);
}


.ys-description-text {
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
  color: rgba(82, 88, 102, 1);
}


.master-catalog-button-outline {
  color: rgba(45, 159, 117, 1);
  padding: 10px;
  gap: 4px;
  border-radius: 10px;
  border: 1px solid rgba(45, 159, 117, 1);
  background: white;
  box-shadow: 0 1px 2px 0 rgba(55, 93, 251, 0.08);

  &:hover {
    background: rgba(45, 159, 117, 1);
    color: white;
  }
}

.master-catalog-button-fill {
  color: white;
  width: 125px;
  height: 40px;
  padding: 10px;
  gap: 4px;
  border-radius: 10px;
  background: rgba(45, 159, 117, 1);
  box-shadow: 0 1px 2px 0 rgba(55, 93, 251, 0.08);

  &:hover {
    background: rgb(12, 59, 46, 1);
  }
}

</style>